import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle, Info, Loader2, Lock, Plus, Save, ShieldCheck, ShieldOff, Trash2,
} from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { getWarrantyTiersSettings, updateWarrantyTiers } from '../../config';
import { useAuth } from '../../context/AuthContext';

// Durées commercialisées — fixées côté serveur
// (`warranty_settings_svc.WARRANTY_YEARS`). Cet écran ne les invente pas, il
// se contente de les afficher : ajouter une durée est un chantier serveur,
// pas l'ajout d'une colonne ici.
const DUREES = [
  { years: 1, label: '+1 an' },
  { years: 2, label: '+2 ans' },
];

const nouvelleCle = () => `tranche-${Math.random().toString(36).slice(2)}`;

const emptyRow = () => ({ key: nouvelleCle(), maxPrice: '', price1: '', price2: '' });

const tiersToRows = (tiers) => (Array.isArray(tiers) ? tiers : []).map((tier) => ({
  key: nouvelleCle(),
  maxPrice: tier?.max_price === null || tier?.max_price === undefined ? '' : String(tier.max_price),
  price1: tier?.prices?.[1] === undefined || tier?.prices?.[1] === null ? '' : String(tier.prices[1]),
  price2: tier?.prices?.[2] === undefined || tier?.prices?.[2] === null ? '' : String(tier.prices[2]),
}));

const parseNombre = (raw) => {
  const parsed = parseFloat(String(raw).trim().replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
};

// Traduit le tableau édité vers la forme attendue par
// `warranty_settings_svc.validate_warranty_tiers`. Aucune validation métier
// n'est refaite ici — bornes croissantes, montants positifs, une seule
// tranche sans plafond : tout ça reste la responsabilité du serveur, seule
// source de vérité (cf. le piège relevé dans le plan sur les tests qui
// mordent pour la mauvaise raison).
const rowsToPayload = (rows) => rows.map((row, index) => {
  const isLast = index === rows.length - 1;
  const maxPriceRaw = row.maxPrice.trim();
  const max_price = isLast && maxPriceRaw === '' ? null : parseNombre(maxPriceRaw);
  return {
    max_price,
    prices: { 1: parseNombre(row.price1), 2: parseNombre(row.price2) },
  };
});

// Repère « Tranche n°X » dans le message renvoyé par le serveur pour signaler
// la ligne fautive — sans redupliquer sa validation.
const trancheIndexFromError = (message) => {
  const match = typeof message === 'string' ? message.match(/Tranche n°(\d+)/) : null;
  return match ? Number(match[1]) - 1 : null;
};

export default function AdminWarrantyTiers() {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('maintenance:update');

  const [settings, setSettings] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await getWarrantyTiersSettings();
      setSettings(data);
      setRows(tiersToRows(data?.tiers));
    } catch (err) {
      setLoadError(err.message || 'Erreur serveur');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRowChange = (index, field, value) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const handleAdd = () => {
    if (!canEdit) return;
    setRows((prev) => [...prev, emptyRow()]);
  };

  const handleRemove = (index) => {
    if (!canEdit) return;
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleReset = () => {
    setSaveError(null);
    setRows(tiersToRows(settings?.tiers));
  };

  const handleSave = async () => {
    if (!canEdit) return;
    setSaving(true);
    setSaveError(null);
    try {
      const data = await updateWarrantyTiers(rowsToPayload(rows));
      setSettings(data);
      setRows(tiersToRows(data?.tiers));
    } catch (err) {
      setSaveError(err.message || 'Erreur serveur');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Garantie Atelier">
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 text-secondary animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  if (loadError) {
    return (
      <AdminLayout title="Garantie Atelier">
        <div
          className="bg-white rounded-sm border border-rose-200 p-6 text-center"
          data-testid="warranty-tiers-load-error"
        >
          <p className="text-sm text-rose-600">{loadError}</p>
        </div>
      </AdminLayout>
    );
  }

  // Les trois états décrits par `warranty_settings_svc` — jamais à confondre :
  // jamais configuré (défaut appliqué), personnalisé (fait foi), vide
  // (interrupteur qui coupe la vente). Calculés depuis le dernier réglage
  // CHARGÉ, pas depuis le brouillon en cours d'édition : c'est ce qui est
  // réellement servi aux clients en ce moment.
  const isDefault = Boolean(settings?.is_default);
  const savedTiers = Array.isArray(settings?.tiers) ? settings.tiers : [];
  const isEmptyLive = !isDefault && savedTiers.length === 0;
  const isActiveCustom = !isDefault && savedTiers.length > 0;

  const erroneousIndex = trancheIndexFromError(saveError);
  const draftIsEmpty = rows.length === 0;

  return (
    <AdminLayout title="Garantie Atelier">
      <div className="max-w-4xl space-y-6">
        {isDefault && (
          <div
            data-testid="warranty-tiers-banner-default"
            className="flex items-start gap-3 rounded-sm border border-sky-200 bg-sky-50 p-4"
          >
            <Info className="w-5 h-5 text-sky-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-primary">Aucun réglage enregistré</p>
              <p className="text-xs text-slate-600 mt-1">
                Le barème par défaut ci-dessous s'applique : la garantie est vendue au tarif
                validé. Enregistrer ce tableau tel quel figera ces valeurs en base.
              </p>
            </div>
          </div>
        )}

        {isEmptyLive && (
          <div
            data-testid="warranty-tiers-banner-empty"
            className="flex items-start gap-3 rounded-sm border border-rose-300 bg-rose-50 p-4"
          >
            <ShieldOff className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-rose-700">Aucune garantie proposée aux clients</p>
              <p className="text-xs text-rose-700/90 mt-1">
                Le barème enregistré est vide : c'est l'interrupteur qui coupe la vente de
                garantie sans redéploiement. Ajoutez au moins une tranche pour la réactiver.
              </p>
            </div>
          </div>
        )}

        {isActiveCustom && (
          <div
            data-testid="warranty-tiers-banner-active"
            className="flex items-start gap-3 rounded-sm border border-emerald-200 bg-emerald-50 p-4"
          >
            <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-primary">Barème personnalisé en vigueur</p>
              {settings?.updated_at && (
                <p className="text-xs text-slate-600 mt-1">
                  Dernière modification {settings.updated_by ? `par ${settings.updated_by} ` : ''}
                  le {new Date(settings.updated_at).toLocaleString('fr-FR')}.
                </p>
              )}
            </div>
          </div>
        )}

        {!canEdit && (
          <div
            data-testid="warranty-tiers-readonly-note"
            className="flex items-center gap-2 text-xs text-slate-500 bg-slate-100 rounded-sm px-4 py-2.5"
          >
            <Lock className="w-3.5 h-3.5" />
            Lecture seule : vous n'avez pas les droits nécessaires pour modifier ce barème.
          </div>
        )}

        {saveError && (
          <div
            data-testid="warranty-tiers-error"
            className="rounded-sm border border-rose-300 bg-rose-50 p-3 text-sm text-rose-700"
          >
            {saveError}
          </div>
        )}

        <div className="bg-white rounded-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left px-4 py-3 text-xs text-slate-600 font-medium">
                    Borne haute (prix du produit)
                  </th>
                  {DUREES.map((duree) => (
                    <th key={duree.years} className="text-left px-4 py-3 text-xs text-slate-600 font-medium">
                      {duree.label}
                    </th>
                  ))}
                  {canEdit && <th className="px-4 py-3" aria-label="Actions" />}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => {
                  const isLast = index === rows.length - 1;
                  const isCapped = isLast && row.maxPrice.trim() !== '';
                  const hasError = erroneousIndex === index;
                  return (
                    <tr
                      key={row.key}
                      data-testid={`warranty-tier-row-${index}`}
                      className={`border-b border-slate-200 last:border-0 ${hasError ? 'bg-rose-50' : ''}`}
                    >
                      <td className="px-4 py-3 align-top">
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={row.maxPrice}
                            disabled={!canEdit}
                            onChange={(e) => handleRowChange(index, 'maxPrice', e.target.value)}
                            placeholder={isLast ? 'Sans plafond' : 'Ex : 600'}
                            data-testid={`warranty-tier-max-${index}`}
                            className="w-32 bg-white border border-slate-200 rounded-sm px-3 py-2 text-sm text-primary outline-none disabled:bg-slate-50 disabled:text-slate-500"
                          />
                          {hasError && (
                            <span
                              data-testid={`warranty-tier-error-flag-${index}`}
                              className="text-rose-600"
                              aria-label="Ligne en erreur"
                              title="Cette tranche est refusée par le serveur — voir le message ci-dessus."
                            >
                              <AlertTriangle className="w-4 h-4" />
                            </span>
                          )}
                        </div>
                        {isCapped && (
                          <p
                            data-testid={`warranty-tier-cap-warning-${index}`}
                            className="mt-1.5 flex items-start gap-1 text-[11px] text-amber-700 max-w-[16rem]"
                          >
                            <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
                            Dernière tranche plafonnée : au-delà de ce prix, aucune garantie
                            n'est proposée aux clients — c'est un choix commercial, assurez-vous
                            qu'il est voulu.
                          </p>
                        )}
                      </td>
                      {DUREES.map((duree) => (
                        <td className="px-4 py-3 align-top" key={duree.years}>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={row[`price${duree.years}`]}
                            disabled={!canEdit}
                            onChange={(e) => handleRowChange(index, `price${duree.years}`, e.target.value)}
                            placeholder="Ex : 49"
                            data-testid={`warranty-tier-price${duree.years}-${index}`}
                            className="w-24 bg-white border border-slate-200 rounded-sm px-3 py-2 text-sm text-primary outline-none disabled:bg-slate-50 disabled:text-slate-500"
                          />
                        </td>
                      ))}
                      {canEdit && (
                        <td className="px-4 py-3 align-top">
                          <button
                            type="button"
                            onClick={() => handleRemove(index)}
                            data-testid={`warranty-tier-remove-${index}`}
                            className="w-8 h-8 rounded-sm bg-slate-100 flex items-center justify-center hover:bg-rose-100 transition-colors"
                            aria-label="Supprimer la tranche"
                          >
                            <Trash2 className="w-3.5 h-3.5 text-slate-600" />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}

                {rows.length === 0 && (
                  <tr>
                    <td colSpan={canEdit ? 4 : 3} className="px-4 py-8 text-center text-sm text-slate-500">
                      Aucune tranche : le barème du tableau est vide.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {canEdit && draftIsEmpty && (
          <div
            data-testid="warranty-tiers-zero-draft-warning"
            className="flex items-start gap-3 rounded-sm border border-rose-300 bg-rose-50 p-4"
          >
            <ShieldOff className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
            <p className="text-sm text-rose-700">
              Vous êtes sur le point d'enregistrer un barème vide : en cliquant sur
              « Enregistrer », plus aucune garantie ne sera proposée aux clients.
            </p>
          </div>
        )}

        {canEdit && (
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={handleAdd}
              data-testid="warranty-tier-add"
              className="inline-flex items-center gap-2 bg-white text-primary border-2 border-secondary hover:bg-secondary hover:text-white font-bold uppercase tracking-wider rounded-sm px-5 py-2.5 text-sm transition-colors"
            >
              <Plus className="w-4 h-4" /> Ajouter une tranche
            </button>

            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              data-testid="warranty-tiers-save"
              className="inline-flex items-center gap-2 bg-white text-primary border-2 border-secondary hover:bg-secondary hover:text-white font-bold uppercase tracking-wider rounded-sm px-6 py-2.5 text-sm transition-colors disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Enregistrer
            </button>

            <button
              type="button"
              onClick={handleReset}
              data-testid="warranty-tiers-reset"
              className="bg-slate-100 text-primary font-medium rounded-full px-5 py-2.5 text-sm hover:bg-white/20 transition-colors"
            >
              Annuler mes modifications
            </button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
